import { Entity, FieldType, getFilter, getRowDataFromKey, PersistenceType } from 'gossamer-unified';
import { InstitutionGroup } from './InstitutionGroup';
import { GROUPS_I_AM_ADMIN_OF } from './SecurityRules';

export interface ReportInstitutionGroupViewRow {
  institution_group_id: number;
  week_commencing: string;
  event_count: number; // total events
  post_event_count: number; // total events with flow_option IN 'attendanceOnly', 'full'
  booking_count: number;
  post_event_booking_count: number; // total bookings for above events
  booking_rate: number;
  capacity: number;
  utilisation_rate: string;
  attendance_count: number;
  attendance_rate: string;
  sum_content_score: number;
  avg_content_score: number;
  sum_delivery_score: number;
  avg_delivery_score: number;
  sum_admin_score: number;
  avg_admin_score: number;
  sum_overall_score: number;
  avg_overall_score: number;
  feedback_count: number;
}

export const ReportInstitutionGroupView: Entity<ReportInstitutionGroupViewRow> = {
  id: 'ReportInstitutionGroupView',
  title: 'Report Institution Group View',
  service: 'main',
  rowLabel: (row: ReportInstitutionGroupViewRow) => [row.institution_group_id, row.week_commencing].join(' / '),
  primaryKeyFields: ['institution_group_id', 'week_commencing'],
  source: {
    tableName: 'public.report_institution_group_view',
  },
  security: [
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: `institution_group_id IN ${GROUPS_I_AM_ADMIN_OF}`,
    },
    { role: 'staff', action: 'read', access: 'all' },
  ],
  defaultSort: [['week_commencing', 'DESC']],
  defaultFilters: [
    { fieldId: 'institution_group_id', operator: 'eq', value: '' },
    { fieldId: 'week_commencing', operator: 'eq', value: '' },
  ],
  listRowLink: (key: string) => {
    const { institution_group_id, week_commencing } = getRowDataFromKey(ReportInstitutionGroupView, key);
    return (
      '/admin/reporting/group-week-user-search?q=' +
      encodeURIComponent(
        JSON.stringify({
          where: {
            and: [
              getFilter('institution_group_id', 'eq', institution_group_id),
              getFilter('week_commencing', 'eq', week_commencing),
            ],
          },
        }),
      )
    );
  },
  fields: {
    institution_group_id: {
      seq: 5,
      label: 'Institution Group',
      type: FieldType.Reference,
      refEntity: InstitutionGroup,
    },
    week_commencing: {
      seq: 10,
      type: FieldType.Date,
      label: 'Week Commencing',
      listColumn: true,
      listColumnAlign: 'right',
    },
    event_count: {
      seq: 15,
      type: FieldType.Integer,
      label: 'All Events',
      listColumn: true,
    },
    booking_count: {
      seq: 20,
      label: 'All Bookings',
      listColumn: true,
      type: FieldType.Integer,
    },
    booking_rate: {
      seq: 25,
      label: 'Booking Rate',
      type: FieldType.Decimal,
      listColumn: true,
      decimalDigits: 2,
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionGroupViewRow) =>
        !row.event_count ? 0 : row.booking_count / row.event_count,
    },
    capacity: {
      seq: 30,
      label: 'Capacity',
      type: FieldType.Integer,
      listColumn: true,
    },
    utilisation_rate: {
      seq: 35,
      label: 'Utilisation Rate',
      type: FieldType.Text,
      listColumn: true,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionGroupViewRow) =>
        !row.capacity ? '-' : ((row.booking_count * 100) / row.capacity).toFixed(0) + '%',
    },
    post_event_count: {
      seq: 40,
      type: FieldType.Integer,
      label: 'Tracked Events',
      listColumn: true,
    },
    post_event_booking_count: {
      seq: 45,
      label: 'Tracked Bookings',
      type: FieldType.Integer,
      listColumn: true,
    },
    attendance_count: {
      seq: 50,
      label: 'Attendances',
      type: FieldType.Integer,
      listColumn: true,
    },
    attendance_rate: {
      seq: 55,
      label: 'Attendance Rate',
      type: FieldType.Text,
      listColumn: true,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionGroupViewRow) =>
        !row.post_event_booking_count
          ? '-'
          : ((row.attendance_count * 100) / row.post_event_booking_count).toFixed(0) + '%',
    },
    sum_content_score: {
      seq: 65,
      label: 'Total Content Score',
      type: FieldType.Decimal,
    },
    avg_content_score: {
      seq: 70,
      label: 'Average Content Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionGroupViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    sum_delivery_score: {
      seq: 75,
      label: 'Total Delivery Score',
      type: FieldType.Decimal,
    },
    avg_delivery_score: {
      seq: 80,
      label: 'Average Delivery Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionGroupViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    sum_admin_score: {
      seq: 85,
      label: 'Total Admin Score',
      type: FieldType.Decimal,
    },
    avg_admin_score: {
      seq: 90,
      label: 'Average Admin Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionGroupViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    sum_overall_score: {
      seq: 95,
      label: 'Total Overall Score',
      type: FieldType.Decimal,
    },
    avg_overall_score: {
      seq: 100,
      label: 'Average Overall Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionGroupViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    feedback_count: {
      seq: 105,
      label: 'Feedback',
      type: FieldType.Integer,
      listColumn: true,
    },
  },
};
